import { defineAsyncComponent, hydrateOnIdle, hydrateOnVisible } from 'vue';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { updateSubscriptionByListing, createNotification } from '@/common/utilities/nojq/_notification.js';
import throttle from 'lodash/throttle';
import get from 'lodash/get';
import format from '@/common/utilities/format';
import propertyFuncs from '@/common/utilities/propertyFuncs';
import { getCookie } from '../../../common/utilities/nojq/_cookie.js';
import { scrollTo } from '../../../common/utilities/nojq/_scrollto.js';

import dppNav from '@/dpp/components/propertynav/dpp-nav.vue';
import dppSchedule from '@/dpp/components/dppschedule/dpp-schedule.vue';
import propertyPopMap from '@/dpp/components/propertypopmap/property-popmap.vue';
import propertyPop3d from '@/dpp/components/propertypop3d/property-pop3d.vue';
import propertyPopPhotoSingle from '@/dpp/components/propertypopphotosingle/property-popphotosingle.vue';
import mvtCollapse from '@/common/components/mvtcollapse/mvt-collapse.vue';
import dppButton from '@/dpp/components/propertybutton/dpp-button/dpp-button.vue';
import dppBar from '@/dpp/components/dppbar/dpp-bar.vue';
import PropertySchema from '@/common/components/schemas/property-scheme.vue';
import mvtHeader from '@/common/components/mvtheader/mvt-header.vue';
import mvtHotleadForm from '@/common/components/mvthotleadform/mvt-hotleadform.vue';
import dppSuggestion from '@/dpp/components/dppsuggestion/dpp-suggestion.vue';
import dppPhoto from '@/dpp/components/dppphoto/dpp-photo.vue';
import propertyTags from '@/dpp/components/propertytags/property-tags.vue';
import propertyDesc from '@/dpp/components/propertydesc/property-desc.vue';
import propertyTourCta from '@/dpp/components/propertytourcta/property-tourcta.vue';
import propertySummaryTitle from '@/dpp/components/propertysummary/property-summary-title.vue';
import propertyOpendoorPanel from '@/dpp/components/propertyopendoor/property-opendoor-panel.vue';
import propertySummaryDetails from '@/dpp/components/propertysummary/property-summarydetails.vue';
import { loadImg as nativeLoadImg } from '@/common/utilities/nojq/_load.img.js';
import { addDebugBearTag } from '@/common/utilities/nojq/_load.thirdparty.scripts.js';
import { setStorage, getStorage } from '../../../common/utilities/nojq/_localstorage.js';
import mvtReorder from '@/common/components/mvtreorder/mvt-reorder.vue';
import { getPathByFilter } from '../../../../widget/spec/filterhelper';
import { resolveClickCta } from '@/common/utilities/nojq/_utilities.js';
import {
    buildOpendoorAddressFromGeo,
    buildOpendoorAcceptedWithEstimateGaLabel,
    getOpendoorValueEstimateCenter,
    getPreferredOpendoorHomeSellProduct,
    logOpendoorAddressDetailsException,
} from '../../../dpp/components/opendoor/opendoor-address.js';

let _dppViewItemGa = null;
function loadDppViewItemGa() {
    if (!_dppViewItemGa) {
        _dppViewItemGa = import(/* webpackChunkName: "msp-ga4" */ '../../../../widget/spec/ga4helper.js').then((m) => ({
            getItemByDpp: m.getItemByDpp,
        }));
    }
    return _dppViewItemGa;
}

/**
 * Lazy hydration aligned with `page-search.js` (MSP): `hydrateOnVisible` for scroll-heavy islands
 * (nearby, map, schools, mortgage, etc.) so idle slices are not spent hydrating the whole vdpp tree
 * before LCP/INP. Tighter nearby margin than before — less work before the user scrolls there.
 */
const mvtListingCarousel = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/common/components/mvtlistingcarousel/mvt-listing-carousel.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '100px' }),
});

const mvtPopSignin = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/common/components/mvtpopsignin/mvt-popsignin.vue'),
    hydrate: hydrateOnIdle(),
});
const mvtDialogCenter = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/common/components/mvtdialogcenter/mvt-dialogcenter.vue'),
    hydrate: hydrateOnIdle(),
});
const mvtFeed = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/common/components/mvtfeed/mvt-feed.vue'),
    hydrate: hydrateOnIdle(),
});
const mvtToastCenter = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/common/components/mvttoastcenter/mvt-toastcenter.vue'),
    hydrate: hydrateOnIdle(),
});
const mvtPopOwnup = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/common/components/mvtpopownup/mvt-popownup.vue'),
    hydrate: hydrateOnIdle(),
});
const mvtDialog = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/common/components/mvtdialog/mvt-dialog.vue'),
    hydrate: hydrateOnIdle(),
});

const propertyFeature = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertyfeature/property-feature.vue'),
    hydrate: hydrateOnIdle(),
});
const mvtBreadcrumb = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/common/components/mvtbreadcrumb/mvt-breadcrumb.vue'),
    hydrate: hydrateOnIdle(),
});
const propertySaleProceeds = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertysaleproceeds/property-saleproceeds.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '80px' }),
});
/** Below hero on active listings; defer hydration until near viewport to reduce idle contention with LCP/INP. */
const propertyEstimate = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertyestimate/property-estimate.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '0px' }),
});
const propertyHistory = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertyhistory/property-history.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '80px' }),
});
const propertyTaxHistory = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertytaxhistory/property-tax-history.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '80px' }),
});
const propertyNeighborhood = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertyneighborhood/property-neighborhood.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '80px' }),
});
/** Market stats + Chart.js: hydrate only once intersecting viewport (no prefetch margin — avoids early `dppmarkettrenddata`). */
const propertyMarket = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertymarket/property-market.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '0px' }),
});
const propertyNearbyHomeValues = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertynearbyhomevalues/property-nearbyhomevalues.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '80px' }),
});
const propertyMortgage = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertymortgage/property-mortgage.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '80px' }),
});
const propertyCommute = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertycommute/property-commute.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '80px' }),
});
const propertySchedule = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertyschedule/property-schedule.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '80px' }),
});
const propertySchool = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertyschool/property-school.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '80px' }),
});
const propertyPoi = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertypoi/property-poi.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '80px' }),
});
const propertyGeoLink = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertygeolink/property-geolink.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '120px' }),
});
const propertyGeoFilterLink = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/propertygeolink/property-geofilterlink.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '120px' }),
});
const dppHotleadButton = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/dpp/components/dpphotleadbutton/dpp-hotleadbutton.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '60px' }),
});
/** Same idea as `searchFooter` in `search-list.js`: hydrate when near viewport, not only on idle. */
const mvtFooter = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/common/components/mvtfooter/mvt-footer.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '200px' }),
});

const mvtAds = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/common/components/mvtads/mvt-ads.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '100px' }),
});

/** SSR emits Product / Application / VirtualTour JSON-LD in `page-dpp.ejs`; client islands only refine — hydrate when near viewport to save idle main-thread time. */
const PrimaryImageSchema = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/common/components/schemas/primary-image-schema.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '0px' }),
});
const ApplicationSchema = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/common/components/schemas/application-schema.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '0px' }),
});
const VirtualTourSchema = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/common/components/schemas/virtualtour-schema.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '0px' }),
});
const DataFeedItemSchema = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vdpp" */ '@/common/components/schemas/data-feed-item-schema.vue'),
    hydrate: hydrateOnVisible({ rootMargin: '0px' }),
});

const MORTGAGE_LINK_REDIRECT_URL = 'https://www.blink.mortgage/app/signup/p/olfinancialllc?campaign=dppprimary ';
const STATUS = {
    before1: 'before1',
    after1: 'after1',
    before2: 'before2',
    after2: 'after2',
};
const autoCost = 30 * 1000;
const timerA = 30 * 1000;
const timerB = 4.5 * 60 * 1000;
const PREAPPROVED_EXPERIMENT = 'movoto-cpc-preapproved-cw-9240';

/** Async to avoid circular init / TDZ in SSR bundle (propertyPopOpendoor pulls heavy Opendoor SDK). */
const propertyPopOpendoor = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "vproperty-popopendoor" */ '@/dpp/components/propertypopopendoor/property-popopendoor.vue'),
});

// const autoCost = 5 * 10000;
// const timerA = 5 * 1000;
// const timerB = 5 * 1000;

function loadImg(imgElement) {
    const imgEl = imgElement;
    const imgSrc = imgEl.getAttribute('data-src');
    if (imgSrc) {
        nativeLoadImg(imgEl, imgSrc);
    }
    imgEl.removeAttribute('data-src');
}

/** Postpone DPP mount housekeeping so it does not extend the hydration long task (TBT). */
function scheduleDeferredDppMountWork(fn) {
    if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(fn, { timeout: 2000 });
    } else {
        setTimeout(fn, 1);
    }
}

var observer = null;
var timer;

function getValueByName(data, name) {
    const stack = [...data];

    while (stack.length > 0) {
        const currentItem = stack.pop();

        if (Array.isArray(currentItem)) {
            stack.push(...currentItem);
        } else if (typeof currentItem === 'object' && currentItem !== null) {
            if ('name' in currentItem && currentItem['name'] === name) {
                return currentItem['value'];
            } else if ('value' in currentItem) {
                stack.push(currentItem['value']);
            }
        }
    }
    return undefined;
}
export default {
    name: 'pageProperty',
    inject: ['$eventBus', 'split'],
    props: ['disableCollapse'],
    components: {
        mvtPopSignin,
        mvtDialogCenter,
        mvtHotleadForm,
        dppSuggestion,
        DataFeedItemSchema,
        PropertySchema,
        PrimaryImageSchema,
        ApplicationSchema,
        VirtualTourSchema,
        dppButton,
        mvtDialog,
        mvtCollapse,
        dppNav,
        propertyFeature,
        propertySaleProceeds,
        dppSchedule,
        dppHotleadButton,
        mvtListingCarousel,
        propertyMortgage,
        propertyPopMap,
        propertyPop3d,
        propertyPopOpendoor,
        propertyPopPhotoSingle,
        mvtBreadcrumb,
        mvtFeed,
        mvtToastCenter,
        mvtHeader,
        mvtFooter,
        mvtAds,
        mvtPopOwnup,
        dppPhoto,
        propertyTags,
        propertyDesc,
        propertySchedule,
        propertyTourCta,
        propertySummaryTitle,
        propertyOpendoorPanel,
        propertySummaryDetails,
        propertyHistory,
        propertyTaxHistory,
        propertyNeighborhood,
        propertySchool,
        propertyPoi,
        propertyEstimate,
        propertyMarket,
        propertyNearbyHomeValues,
        propertyCommute,
        propertyGeoLink,
        propertyGeoFilterLink,
        mvtReorder,
        dppBar,
    },
    data() {
        return {
            vaLoanCTA: 'Prequalify for a 0% Down VA Loan',
            treatment: {},
            activeChat: false,
            popPhotoCateIndex: 0,
            popPhotoIndex: 0,
            pop3dLoaded: false,
            pop3dUrl: '',
            popOpendoorLoaded: false,
            popMapLoaded: false,
            popPhotoLoaded: false,
            popMapTypeId: null,
            popStreetView: false,
            searchInput: '',
            inActive: true,
            isScrolling: false,
            timer: null,
            isNewUser: false,
            createAccNoti: null,
            popHotPanelTitle: '',
            popHotPanelContent: '',
            showSavedSearchLink: false,
            openIACProvider: false,
            componentMounted: false,
            assignedAgent: null,
            /** When false, `propertyMarket` / `mvtEstPriceChart` fetch 5Y trends if missing; avoids duplicate fetch vs former parent idle prefetch. */
            isMarket5YLoaded: false,
            collpaseVeteran: true,
            isHeaderPinned: false,
            opendoorPanelVisible: false,
            opendoorAddressDetails: null,
            preapprovedTreatment: null,
        };
    },
    computed: {
        ...mapGetters('glb', ['glb', 'hasAgent', 'viewport', 'getPhoneNumber', 'isBrokerageArea', 'viewedListings', 'getSplit', 'removeAds']),
        ...mapGetters('dpp', ['dpp', 'getPhotos', 'getEstPrice', 'getEstimateInfo', 'getCategories', 'getCateIndex', 'getEstRentFee', 'getMonthTotal', 'getHotleadLabel2']),
        ...mapGetters('msp', ['msp', 'getSchoolRequest']),
        showDppAds() {
            return !this.removeAds && this.getSplit('movoto-dpp-ads-CW-4649', 'on');
        },
        rentalCaouselText() {
            if (this.dpp.isRentals) {
                if (this.dpp.geo.neighborhoodPath) {
                    return `${this.dpp.propertyTypeDisplay}s for rent in ${this.dpp.geo.neighborhood}`;
                }
                return `${this.dpp.propertyTypeDisplay}s for rent in ${this.dpp.geo.zipcode}`;
            }
            return '';
        },
        rentalCaouselLink() {
            if (this.dpp.isRentals) {
                let filterPath = getPathByFilter({
                    propertyType: [this.dpp.propertyType],
                });
                if (this.dpp.geo.neighborhoodPath) {
                    return `${this.glb.appUrl}${this.dpp.geo.neighborhoodPath}${filterPath}`;
                }
                return `${this.glb.appUrl}${this.dpp.geo.zipcodePath}${filterPath}`;
            }
            return '';
        },
        inlineHotleadOpt() {
            return {
                hotleadType: 'ListingAgentQuestionResponsiveDPP',
                propertyId: this.dpp.propertyId,
                hideProfile: true,
                propertyData: this.dpp,
                showComment: false,
                ojoFormConfigs: {
                    showCtaLinks: false,
                    showOJOagentInfo: false,
                    showDisclaimer: true,
                    commnetLabel: 'Comments',
                },
                buttonText: this.molang('dpp.propertyAgents.askQuestion'),
                hideIncentive: true,
            };
        },
        secondaryCtaTxt() {
            return this.molang('dpp.hotButton.msgAgent');
        },
        hasOJOAgent() {
            return this.glb.agent.id && !this.glb.rentals;
        },
        showHelocAsidePanel() {
            return this.preapprovedTreatment?.[PREAPPROVED_EXPERIMENT] === 'on17';
        },
        preApprovalButtonObj() {
            var utm, url;
            if (['MOBILE'].includes(this.dpp.propertyType)) {
                utm = 'getpreapproved_mobile';
                url = 'preapprove';
            }
            if (this.dpp.price < 100000) {
                const mortgageCTAbelow100K = this.dpp.houseRealStatus === 'ACTIVE' && this.dpp.price >= 50000 && ['SINGLE_FAMILY_HOUSE', 'CONDO'].includes(this.dpp.propertyType);
                if (!mortgageCTAbelow100K) {
                    return null;
                }
            }
            if (this.glb.geo.state === 'NY') {
                utm = 'getpreapproved';
                url = 'betterfinance';
            } else {
                utm = 'getpreapproved';
                url = 'preapprove';
            }
            let cta = this.dpp.isActive ? this.molang('dpp.propertyDetails.getPreapproved') : this.molang('dpp.propertyDetails.getLowerPaymentNew');
            if (this.showHelocAsidePanel) {
                cta = 'Check Eligibility';
            }
            return {
                cta: cta,
                utmcta: utm,
                url: url,
            };
        },
        ownUpPropertyType() {
            //SingleFamily | Condo | MultiFamily | Townhome | Manufactured | Mobile
            let dict = {
                SINGLE_FAMILY_HOUSE: 'SingleFamily',
                CONDO: 'Condo',
                MULTI_FAMILY: 'MultiFamily',
                MOBILE: 'Townhome',
            };
            return dict[this.dpp.propertyType] || 'SingleFamily';
        },
        detailOrder() {
            let config = [];
            if (this.dpp.isActive) {
                config = [
                    'propertyDetailPanel',
                    'dppAdBelowDetails',
                    'propertySchedulePanel',
                    'propertySchoolPanel',
                    'propertyMapPanel',
                    'propertyBreadcrumbPanel',
                    'propertyPoiPanel',
                    'propertyCommutePanel',
                    'propertyMortgagePanel',
                    'propertyEstimatePanel',
                    'propertyHistoryPanel',
                    'propertyTaxHistoryPanel',
                    'propertyMarketPanel',
                    'propertyCashOfferPanel',
                    'propertySaleProceedsPanel',
                    'propertyVeteranPanel',
                    'propertyInlineHotlead',
                    'dppAsidePanel',
                    'dppAdAboveNearby',
                    'nearbyHomes',
                    'nearbySoldHomes',
                    'nearbyNewHomes',
                    'nearbyRentHomes',
                    'propertyGeoLink',
                    'propertyGeoFilterLink',
                    'dppSchedule',
                ];
                if (this.showDppInlineCommentSuggestTreatment) {
                    //swap propertyInlineHotlead and propertySchedulePanel if there is propertySchedulePanel
                    const propertySchedulePanelIndex = config.indexOf('propertySchedulePanel');
                    const propertyInlineHotleadIndex = config.indexOf('propertyInlineHotlead');
                    if (propertySchedulePanelIndex !== -1 && propertyInlineHotleadIndex !== -1) {
                        config[propertySchedulePanelIndex] = 'propertyInlineHotlead';
                        config[propertyInlineHotleadIndex] = 'propertySchedulePanel';
                    }
                }
            } else {
                config = [
                    'propertyOpendoorPanel',
                    'propertyDetailPanel',
                    'dppAdBelowDetails',
                    'propertyEstimatePanel',
                    'dppAsidePanel',
                    'propertyHistoryPanel',
                    'propertyTaxHistoryPanel',
                    'propertySaleProceedsPanel',
                    'propertyMarketPanel',
                    'propertyNearbyHomeValuesPanel',
                    'propertyCashOfferPanel',
                    'propertySchoolPanel',
                    'propertyMapPanel',
                    'propertyPoiPanel',
                    'propertyMortgagePanel',
                    'propertyInlineHotlead',
                    'propertyVeteranPanel',
                    'propertyCommutePanel',
                    'nearbySoldHomes',
                    'dppAdAboveNearby',
                    'nearbyHomes',
                    'nearbyNewHomes',
                    'nearbyRentHomes',
                    'propertyGeoLink',
                    'propertySchedulePanel',
                    'propertyGeoFilterLink',
                    'propertyBreadcrumbPanel',
                    'dppSchedule',
                ];
            }
            return config;
        },
        listAgent() {
            if (this.dpp.listingAgent) {
                return this.dpp.listingAgent;
            } else if (this.dpp.agentDetails) {
                return this.dpp.agentDetails.agentContactInfo;
            }
        },
        buyingOffice() {
            return getValueByName(this.dpp.features, 'Buying Office');
        },
        listOffice() {
            return this.dpp.officeListName;
        },
        showFirmUnderTitle() {
            const compliance = this.getComplianceMethod()();
            return compliance[0] && compliance[0].rule && compliance[0].rule.indexOf('showFirmUnderTitle') >= 0;
        },
        fields() {
            return propertyFuncs.getFields(this.dpp);
        },
        isOffMarket() {
            if (this.dpp.houseRealStatus === this.glb.propertyStatus.offMarket) {
                return true;
            }
            return false;
        },
        showIncentive() {
            return !this.glb.agent.id && !this.dpp.listingByMovoto;
        },
        showNearbyImgLayout() {
            return !this.dpp.isActive && this.isNewUser;
        },
        getCreditMonitoringText() {
            return 'Check Your Credit Before You Rent';
        },
        showVeteran() {
            return !this.dpp.isRDC && !this.dpp.isRentals && this.dpp.isActive && this.getSplit('movoto-va-CW-4020');
        },
        isForSaleActive() {
            return this.dpp.isActive && !this.dpp.isRentals;
        },
        dppScheduleOpt() {
            let opts = {
                enableChangeDate: !this.dpp.isRDC,
                date: undefined,
                optionalDate: true,
                hotleadTitle: this.dpp.isRDC ? this.molang('dpp.hotleadForm.askBuilder') : this.molang('dpp.hotButton.requestTour'),
                hotleadSub: this.dpp.isRDC ? '' : this.molang('dpp.propertySchedule.seeThisHome'),
                hotleadType: 'InlineHotleadInResponsiveDPP',
                propertyId: this.dpp.propertyId,
                trigger: 'propertyTour',
                hideProfile: true,
                propertyData: this.dpp,
                showComment: false,
                showConcierge: true,
                showAddressAndImage: true,
                buttonText: this.molang('dpp.hotleadForm.submit'),
            };
            return opts;
        },
        ctaRequestTourSubtext() {
            const splitValue = this.glb.splits['movoto-dpp-cta-button-CW-4438'] ?? null;
            switch (splitValue) {
                case 'on1':
                    return 'See it in person';
                case 'on2':
                    return 'No obligation';
                case 'on3':
                    return 'Always free';
                case 'on4':
                    return 'Pick your time';
                case 'on5':
                    return 'See before it’s gone';
                case 'on6':
                    return 'Tour with local expert';
                case 'on7':
                    return 'No spam, no pressure';
                case 'on8':
                    return 'Experience in person';
                case 'on9':
                    return 'Experience this home in person';
                case 'on10':
                    return 'No obligation';
                case 'on11':
                    return 'Always free';
                case 'on12':
                    return 'No obligation';
                case 'on13':
                    if (!this.glb.isPhone) {
                        return 'No credit score too low';
                    }
                    return null;
                case 'on14':
                    if (!this.glb.isPhone) {
                        return 'See it in person';
                    }
                    return null;
                case 'on15':
                    if (!this.glb.isPhone) {
                        return "See it before it's gone";
                    }
                    return null;
                case 'on16':
                    if (!this.glb.isPhone) {
                        return 'Tour for free, no commitment';
                    }
                    return null;
                default:
                    return null;
            }
        },
        showDppInlineCommentSuggestTreatment() {
            return this.getSplit('movoto-comment-CW-3773') && this.dpp.isActive && (!this.dpp.openHouses || this.dpp.openHouses.length === 0);
        },
    },

    methods: {
        ...mapGetters('glb', ['getComplianceMethod']),
        ...mapActions('glb', ['updateFavorite', 'addSaveSearch', 'addClaimedHome', 'getCpsProfileById', 'getAddressDetailsByOpendoor']),
        ...mapMutations('glb', ['addViewedPropertyId']),
        ...mapMutations('dpp', ['updateCateIndex']),
        async loadOpendoorAddressDetails() {
            const address = buildOpendoorAddressFromGeo(this.dpp && this.dpp.geo, { includeStreet2: false });
            if (!address) {
                this.opendoorPanelVisible = false;
                this.$eventBus.$emit('ga', { ec: 'initial_impression', en: 'opendoor', el: 'error_no_address' });
                return;
            }
            try {
                const opendoorDetails = await this.getAddressDetailsByOpendoor(address);
                this.opendoorAddressDetails = getPreferredOpendoorHomeSellProduct(opendoorDetails);
                if (this.opendoorAddressDetails && this.opendoorAddressDetails.isEligible) {
                    this.opendoorPanelVisible = true;
                    const odCenter = getOpendoorValueEstimateCenter(this.opendoorAddressDetails.valueEstimate);
                    if (odCenter != null && Number.isFinite(odCenter)) {
                        const el = buildOpendoorAcceptedWithEstimateGaLabel(this.dpp, this.getEstimateInfo, odCenter);
                        this.$eventBus.$emit('ga', { ec: 'initial_impression', en: 'opendoor', el });
                    } else {
                        this.$eventBus.$emit('ga', { ec: 'initial_impression', en: 'opendoor', el: 'accepted_without_estimate' });
                    }
                } else {
                    this.$eventBus.$emit('ga', { ec: 'initial_impression', en: 'opendoor', el: 'rejected' });
                    this.opendoorPanelVisible = false;
                }
            } catch (err) {
                this.$eventBus.$emit('ga', { ec: 'initial_impression', en: 'opendoor', el: 'error' });
                logOpendoorAddressDetailsException('Opendoor Internal Error', err);
                this.opendoorPanelVisible = false;
            }
        },
        toggleVeteran() {
            this.collpaseVeteran = !this.collpaseVeteran;
        },
        showBuyerAgentCTA() {
            const compliance = this.getComplianceMethod()();
            let compRule = compliance[0] && compliance[0].rule;
            return compRule && compRule.indexOf('showBuyerAgentCTA') >= 0;
        },
        viewItem() {
            loadDppViewItemGa().then(({ getItemByDpp }) => {
                this.$eventBus.$emit('ga4', { event: 'view_item' }, { eventData: { value: this.dpp.price, items: [getItemByDpp(this.dpp)] } });
            });
        },
        setMediaPrice() {
            //get latest three visited this.dpp.price form localstorage
            if (this.dpp.price > 0 && this.dpp.isActive) {
                let threeVisited = getStorage('threeVisited') || [];

                if (!Array.isArray(threeVisited)) {
                    threeVisited = [];
                }

                threeVisited.push(this.dpp.price);

                if (threeVisited.length > 3) {
                    threeVisited.splice(0, threeVisited.length - 3);
                }

                if (threeVisited.length === 3) {
                    //insert avg as the first element, when more then 3 visited
                    threeVisited.unshift(Math.round(threeVisited.reduce((a, b) => a + b, 0) / threeVisited.length));
                }

                setStorage('threeVisited', threeVisited);
            }
        },
        showForm(preApprovalButtonObj, evt) {
            if (!preApprovalButtonObj) {
                console.error('Invalid preApprovalButtonObj provided to showForm');
                return;
            }
            const cta = preApprovalButtonObj.cta;
            const TREATMENT_CONFIG = {
                on12: {
                    utm_campaign: 'getpreapproved_sub100',
                    cta: cta,
                    ga: 'partner_mortgage_listings_session_sub100k',
                },
                on13: {
                    utm_campaign: 'Get Preapproved_inactive',
                    cta: cta,
                    ga: 'partner_mortgage_listings_session',
                    desc: 'getpreapproved',
                },
                on6: {
                    utm_campaign: 'getpreapproved',
                    cta: cta,
                    ga: 'partner_mortgage_listings_session',
                    desc: 'getpreapproved',
                },
                on10: {
                    utm_campaign: 'getpreapproved3T',
                    cta: cta,
                    ga: 'partner_mortgage_listings_session',
                    desc: 'getpreapproved',
                },
            };

            try {
                const treatmentKey = this.preapprovedTreatment?.[PREAPPROVED_EXPERIMENT];
                const opt = TREATMENT_CONFIG[treatmentKey];

                if (opt?.utm_campaign && opt?.ga && opt?.cta) {
                    this.$eventBus.$emit('dialog-center', {
                        name: 'mrcMortgageRates',
                        trigger: this.$options.name,
                        opt: {
                            showNormalMrc: true,
                            ...opt,
                        },
                    });
                    return;
                }
                if (this.preapprovedTreatment?.[PREAPPROVED_EXPERIMENT] === 'on15') {
                    preApprovalButtonObj.utmcta = 'getpreapproved';
                }

                this.show3rdPartyForm1(preApprovalButtonObj.utmcta, preApprovalButtonObj.url, cta, evt);
            } catch (error) {
                console.error('Error in showForm:', error);
                this.show3rdPartyForm1(preApprovalButtonObj.utmcta, preApprovalButtonObj.url, cta, evt);
            }
        },
        show3rdPartyFormFromDppTitle(utmcta, url, cta, isAppendUtmCta = false) {
            var pid;
            if (this.dpp.isActive && this.dpp.price <= 100000) {
                if (this.dpp.propertyType === 'MOBILE') {
                    pid = 'movoto_mobile';
                } else if (this.dpp.propertyType === 'LAND') {
                    pid = 'movoto_land';
                }
            }
            this.$eventBus.$emit('eventbus-pop-3rdparty', {
                pid: pid,
                id: utmcta,
                url: url,
                zipcode: this.dpp.geo.zipcode,
                price: this.dpp.price,
                propertyType: this.dpp.propertyType,
                houseRealStatus: this.dpp.houseRealStatus,
                trigger: this.$options.name,
                cta: cta,
                imageUrl: this.dpp.tnImgPath,
                listingUrl: (this.dpp.path && `${this.glb.appUrl}${this.dpp.path}`) || '',
                isAppendUtmCta: isAppendUtmCta,
            });
        },
        openChatPop1() {
            setStorage('chatPoHotleadStatus', STATUS.before1);
            setTimeout(() => {
                if (document.hidden) {
                    return;
                }
                this.openChat();
                setStorage('chatPoHotleadStatus', STATUS.after1);
                setTimeout(() => {
                    this.closeChat();
                }, autoCost);
            }, timerA);
        },
        openChatPop2() {
            setStorage('chatPoHotleadStatus', STATUS.before2);
            setTimeout(() => {
                if (document.hidden) {
                    return;
                }
                this.openChat();
                setStorage('chatPoHotleadStatus', STATUS.after2);
                setTimeout(() => {
                    this.closeChat();
                }, autoCost);
            }, timerB);
        },
        autoBottomChat() {
            var nextBottomChatPopTime = getStorage('nextBottomChatPopTime');
            var nextBottomChatPopWaitTime = getStorage('nextBottomChatPopWaitTime');
            var now = +new Date();
            if (!nextBottomChatPopWaitTime) {
                nextBottomChatPopWaitTime = 3600000;
            }
            if (!nextBottomChatPopTime) {
                nextBottomChatPopTime = now + nextBottomChatPopWaitTime;
                setStorage('chatPoHotleadStatus', STATUS.before1);
            }
            if (nextBottomChatPopTime < now) {
                setStorage('chatPoHotleadStatus', STATUS.before1);
                if (nextBottomChatPopWaitTime === 3600000) {
                    nextBottomChatPopWaitTime = 86400000;
                } else if (nextBottomChatPopWaitTime === 86400000) {
                    nextBottomChatPopWaitTime = 604800000;
                } else if (nextBottomChatPopWaitTime === 604800000) {
                    nextBottomChatPopWaitTime = 2592000000;
                } else if (nextBottomChatPopWaitTime === 2592000000) {
                    nextBottomChatPopWaitTime = 31536000000;
                }
                nextBottomChatPopTime = now + nextBottomChatPopWaitTime;
            }

            let status = getStorage('chatPoHotleadStatus');
            if (status === STATUS.before1) {
                this.openChatPop1();
            } else if (status === STATUS.before2 || status === STATUS.after1) {
                this.openChatPop2();
            }

            if (nextBottomChatPopTime) {
                setStorage('nextBottomChatPopTime', nextBottomChatPopTime);
            }
            if (nextBottomChatPopWaitTime) {
                setStorage('nextBottomChatPopWaitTime', nextBottomChatPopWaitTime);
            }
        },
        openChatPop() {
            this.$eventBus.$emit('dialog-center', {
                name: 'mvtHotleadForm',
                trigger: this.$options.name,
                opt: {
                    hotleadType: !this.dpp.isActive ? 'OffMarketCombinationSell' : 'AutoPopupHotleadInResponsiveDPP',
                    buttonText: this.molang('dpp.hotleadForm.submit'),
                    propertyId: this.dpp.propertyId,
                    propertyData: this.dpp,
                    hotleadTitle: 'Connect with a local Real Estate Agent',
                    hideVeteran: true,
                    profileUrl: 'icon-user-home',
                    profileHighlighter: true,
                },
            });
            this.closeChat();
        },
        closeChat() {
            this.activeChat = false;
            var status = getStorage('chatPoHotleadStatus');
            if (status === STATUS.after1) {
                this.openChatPop2();
            }
        },
        toggleChat() {
            if (this.activeChat) {
                this.closeChat();
            } else {
                this.openChat();
            }
        },
        openChat() {
            this.activeChat = true;
        },

        showTransDialog() {
            this.$refs.dppTransPop.show('dppTrans', this.$options.name, { lite: true });
        },

        openAvmDisclaimer3(target) {
            // this.$eventBus.$emit('dialog-center', { name: 'dppPopAvm', opt: {}, trigger: this.$options.name });

            this.$refs.dppAvmDisclaimer3.show('dppAvmDisclaimer3', target || this.$options.name);
        },
        openAvmDisclaimer4(target) {
            // this.$eventBus.$emit('dialog-center', { name: 'dppPopAvm', opt: {}, trigger: this.$options.name });

            this.$refs.dppAvmDisclaimer4.show('dppAvmDisclaimer4', target || this.$options.name);
        },
        openHotDialog(data) {
            this.popHotPanelContent = data.content;
            this.popHotPanelTitle = data.title;
            this.$refs.dppHotPop.show('dppHotPop', this.$options.name, {});
        },
        openHotlead(buttonText) {
            let hotleadType = 'GoSeeThisHomeHotleadInResponsiveDPP';
            const listingAgentData = this.dpp.listingByMovoto && this.dpp.listingAgentData;
            let showCommentBox = false;
            let subCopy = '';
            let hideProfile = false;
            let hotleadTitle;
            let hotleadSub;
            if (!this.dpp.isActive) {
                hotleadType = this.newSoldPublicRecordCta ? 'RequestSellingConsultation_SoldPRDPP' : 'OffMarketCombinationSell';
                hotleadSub = 'Talk to a top local agent about selling your home today. No obligation.';
                if (buttonText == this.molang('dpp.hotButton.msgAgent')) {
                    hotleadTitle = 'Message an Agent About Selling';
                }
                hotleadTitle = this.newSoldPublicRecordCta && 'Schedule a selling consultation';
            } else if (buttonText == this.molang('dpp.hotButton.msgAgent') || buttonText == this.molang('dpp.hotButton.askAgent')) {
                hotleadTitle = buttonText;
                if (this.showBuyerAgentCTA()) {
                    hotleadTitle = this.molang('dpp.hotleadForm.msgOjoAgent');
                }
                var deviceType = this.glb.isMobile ? 'Mobile' : '';
                if (this.dpp.houseRealStatus === 'ACTIVE' || this.dpp.houseRealStatus === 'PENDING') {
                    if (deviceType) {
                        hotleadType = '_FixedHotleadInResponsiveDPPMobile';
                    } else {
                        hotleadType = 'FixedHotleadInResponsiveDPP';
                    }
                } else if (this.dpp.houseRealStatus === 'SOLD') {
                    hotleadType = '_FixedHotleadInResponsiveSoldDPP' + deviceType;
                } else if (this.dpp.houseRealStatus === 'OFF_MARKET') {
                    hotleadType = '_FixedHotleadInResponsivePrDPP' + deviceType;
                }
                subCopy = this.getSplit('movoto-dpp-message-agent-support-CW-4532') ? this.molang('dpp.hotButton.messageAgentLeadSupport') : '';
                this.$eventBus.$emit('ga', {
                    ec: 'dppHotleadButton',
                    en: 'click_custom',
                    el: 'Message_Agent',
                    props: { link_name: 'Message Agent' },
                });
                buttonText = this.molang('dpp.hotleadForm.go');
            } else if (buttonText == this.molang('dpp.hotButton.requestTour')) {
                const testRequestTour = this.dpp.isActive;
                const timePickerSplit = this.getSplit('movoto-request-tour-CW-4531', 'on');
                this.$eventBus.$emit('property-pop-schedule', {
                    date: testRequestTour ? undefined : new Date(),
                    hotleadType: 'TourAsPrimaryCTA',
                    changeDate: true,
                    optionalDate: testRequestTour ? true : false,
                    hideProfile: testRequestTour,
                    showAddressAndImage: testRequestTour,
                    hotleadTitle: testRequestTour ? this.molang('dpp.hotButton.requestTour') : this.molang('dpp.propertyChat.scheduleTour'),
                    hotleadSub: testRequestTour ? this.molang('dpp.propertySchedule.seeThisHome') : '',
                    trigger: testRequestTour ? 'propertyTour' : this.$options.name,
                    buttonText: testRequestTour ? this.molang('dpp.hotleadForm.submit') : 'Request Appointment',
                    showTourTimePicker: timePickerSplit,
                });
                this.$eventBus.$emit('ga', { ec: 'dppHotleadButton', en: 'click_custom', el: 'Request_Tour', props: { link_name: 'Request Tour' } });
                return;
            } else {
                if (this.showBuyerAgentCTA()) {
                    hotleadTitle = this.molang('dpp.hotleadForm.connectBuyerAgent');
                }
            }
            let btnText = this.showBuyerAgentCTA() ? this.molang('dpp.hotleadForm.go') : buttonText || this.molang('dpp.hotButton.requestInfo');

            this.$eventBus.$emit('dialog-center', {
                name: 'mvtHotleadForm',
                trigger: this.$options.name,
                opt: {
                    hotleadTitle: hotleadTitle,
                    hotleadSub: hotleadSub || subCopy || undefined,
                    hotleadType: hotleadType,
                    propertyId: this.dpp.propertyId,
                    propertyData: this.dpp,
                    agentData: listingAgentData,
                    buttonText: btnText,
                    showComment: showCommentBox,
                    hideProfile: hideProfile,
                    showBuySold: true,
                },
            });
        },
        sendEmail() {
            this.$eventBus.$emit('property-share');
        },
        initalAutoPop() {
            if (window.location.hash.includes('#autosave')) {
                this.$eventBus.$emit('eventbus-action-save-home');
            }

            var matched = window.location.hash.match(/(opensv|virtualtour|virtualtourappt)=(\w{3}_\d{2})/);
            if (matched && matched.length === 3) {
                var type = matched[1];
                var sv = matched[2].split('_');
                let changeDate = type === 'virtualtourappt';
                var timeMapping = {
                    JAN: 0,
                    FEB: 1,
                    MAR: 2,
                    APR: 3,
                    MAY: 4,
                    JUN: 5,
                    JUL: 6,
                    AUG: 7,
                    SEP: 8,
                    OCT: 9,
                    NOV: 10,
                    DEC: 11,
                };
                var year = new Date().getFullYear();
                var month = timeMapping[sv[0].toUpperCase()];
                let date = new Date(`${month + 1}/${sv[1]}/${year}`);
                var min = new Date();
                min.setHours(0, 0, 0, 0);
                var max = new Date(min);
                max.setDate(min.getDate() + 14);
                let isVirtualTourPrompt = ['virtualtour', 'virtualtourappt'].indexOf(type) !== -1 || (type == 'opensv' && date < min);
                if (date < min || date >= max) {
                    date = min;
                }
                // ask what is UrlPrompt & enableUpdate ?
                let hotleadType = isVirtualTourPrompt ? 'VirtualTourHotleadInResponsiveDPP' : 'openHouseCalendarHotlead';
                this.openSchedule({
                    date,
                    hotleadType,
                    changeDate,
                    trigger: this.$options.name,
                });
            }

            var search = window.location.search;
            if (search) {
                if (search.indexOf('popphoto=1') > -1) {
                    this.popPhoto();
                } else if (search.indexOf('pop3d=1') > -1) {
                    this.pop3d(this.dpp.threeDTourLink || this.dpp.videoTourLink);
                } else if (search.indexOf('popmap=1') > -1) {
                    this.popMap();
                } else if (search.indexOf('poptrans=1') > -1) {
                    this.showTransDialog();
                } else if (search.indexOf('popshare=1') > -1) {
                    this.sendEmail();
                } else if (search.indexOf('school=1') > -1) {
                    setTimeout(() => {
                        document.getElementById('propertySchoolPanel').scrollIntoView();
                    }, 100);
                }
            }
        },
        _initalLazyload() {
            var that = this;
            var $scroller = $(this.$refs['scroller']);
            $scroller.find('[data-src]').each(function (i, e) {
                that.setImg(e);
            });
        },
        sendCashOffer() {
            this.$eventBus.$emit('dialog-center', {
                name: 'mvtCashOffer',
                trigger: 'mvtSellPanel',
                opt: {
                    hotleadType: 'CashOfferBanner',
                    buttonText: this.molang('dpp.hotleadForm.submit'),
                },
            });
        },
        sendSellLead(btnText, title) {
            this.$eventBus.$emit('ga', { ec: 'partner', en: 'orchard_session', el: 'dppclick', props: { link_name: title } });
            this.$eventBus.$emit('dialog-center', {
                name: 'mvtSellLead',
                trigger: this.$options.name,
                opt: {
                    hotleadTitle: title || 'Enter your current address',
                    hotleadSub: null,
                    buttonText: btnText || this.molang('dpp.hotleadForm.submit'),
                    hotleadType: 'HaveAHomeToSell',
                },
            });
        },
        setImg(img) {
            var scroller = this.$refs['scroller'];
            if (window.IntersectionObserver) {
                if (!observer) {
                    const options = {
                        root: scroller,
                        rootMargin: '50% 100% 50% 100%',
                        threshold: 0,
                    };
                    observer = new IntersectionObserver(function (entries) {
                        entries.forEach((entry) => {
                            if (!entry.isIntersecting) {
                                return;
                            }
                            loadImg(entry.target);
                            observer.unobserve(entry.target);
                        });
                    }, options);
                }
                observer.observe(img);
            } else {
                loadImg(img);
            }
        },
        onLeftScroll() {
            var $tagPanel = $('.dpp-photo-label').find('.tag-group.fade');
            $tagPanel.addClass('out');
            if (timer) {
                clearTimeout(timer);
            }
            timer = setTimeout(function () {
                $tagPanel.removeClass('out');
            }, 1000);
        },
        openSchedule({ date, hotleadType, hotleadTitle, hotleadSub, changeDate, optionalDate, trigger, buttonText, hideProfile, showAddressAndImage, showTourTimePicker }) {
            this.$refs.viewSchedule.makeSchedule({ date, hotleadType, hotleadTitle, hotleadSub, changeDate, optionalDate, buttonText, hideProfile, showAddressAndImage, showTourTimePicker }, trigger);
        },
        openPopPhotoSingle(data) {
            this.$refs.popPhotoSingle.open(data.cateIndex, data.photoIndex);
        },
        openPopPhoto(data) {
            var that = this;
            if (!this.popPhotoLoaded) {
                this.popPhotoLoaded = true;
                this.popPhotoCateIndex = data.cateIndex;
                this.popPhotoIndex = data.photoIndex;
                this.popPhotoSingle = data.single;
            }
            setTimeout(function () {
                that.$refs.popPhotoSingle.open(data.cateIndex, data.photoIndex, data.single);
            }, 1);
        },
        openPop3d(url) {
            if (!this.pop3dLoaded) {
                this.pop3dLoaded = true;
                this.pop3dUrl = url;
            } else {
                this.$refs.pop3d.open(url);
            }
        },
        openPopOpendoor() {
            if (!this.popOpendoorLoaded) {
                this.popOpendoorLoaded = true;
            } else {
                this.$refs.popOpendoor.open();
            }
        },
        openPopMap(data) {
            if (!this.popMapLoaded) {
                this.popMapLoaded = true;
                this.popMapTypeId = data.popMapTypeId;
                this.popStreetView = data.popStreetView;
                this.popFilter = data.onlyPoi;
                this.popActiveMarker = data.activeMarker;
            } else {
                if (data) {
                    this.$refs.popMap.open(data.popMapTypeId, data.popStreetView, data.popFilter, data.activeMarker);
                }
            }
        },
        pop3d(url) {
            this.openPop3d(url);
        },

        popPhoto() {
            this.openPopPhoto({ cateIndex: 0, photoIndex: 0, single: !this.glb.isPhone });
        },
        popMap() {
            this.openPopMap({ popMapTypeId: 0, popStreetView: false });
        },
        popStreet() {
            this.openPopMap({ popMapTypeId: 0, popStreetView: true });
        },
        async loadAssignedAgent() {
            const movotoAgentCpsId = this.glb.agent.id;
            try {
                if (movotoAgentCpsId) {
                    const response = await this.getCpsProfileById({ id: movotoAgentCpsId, fetchRelationships: false });
                    if (response && response.data && response.data.credentials) {
                        let licenses = response.data.credentials;
                        const issuedArea = licenses.length && licenses.map((item) => item.state);
                        if (issuedArea && issuedArea.includes(this.dpp.geo && this.dpp.geo.state)) {
                            this.assignedAgent = response.data;
                        }
                    } else {
                        throw new Error(get(response, 'status.errorDetail', 'Unknown Error'));
                    }
                }
            } catch (err) {
                // console.error(`An error occurred loading the assigned agent for user: ${movotoUserId}`, err);
            }
        },
        saveFavorite(trigger) {
            let dppData = {
                formatMlsInfo: this.dpp.formatMlsInfo,
                geo: this.dpp.geo,
                mlsDescription: this.dpp.mlsDescription,
                mlsLogoSrc: this.dpp.mlsLogoSrc,
                photoCount: this.dpp.photoCount,
                tnImgPath: this.dpp.tnImgPath,
            };
            let options = {
                propertyData: dppData,
                propertyId: this.dpp.propertyId,
                listingId: this.dpp.listingId,
                isPrOnly: this.dpp.isPrOnly,
                isFavorite: !(this.glb.favoriteListings.indexOf(this.dpp.propertyId) >= 0),
                trigger: trigger,
                lite: true,
                estPrice: this.dpp.isPrOnly ? this.getEstPrice : 0,
            };
            let hasLogin = !!this.glb.user.id;
            setStorage('pendingRequestName', 'addfavorite');
            setStorage('favoriteHome', JSON.stringify(options));
            this.updateFavorite(options).then((res) => {
                if (res.status && res.status.code === 0 && options.isFavorite) {
                    this.$eventBus.$emit('dialog-center-tip', `<span class="text-bold">Success!</span> <span class="text-medium">Home saved.</span>`);
                }
                if (options.isFavorite && +getStorage('hotleadCount') == 0 && !(this.glb.hotleadInfo.phone || this.glb.user.phone) && !this.dpp.isRentals) {
                    this.$eventBus.$emit('eventbus-pop-schedule-lead');
                } else if (hasLogin) {
                    if (options.isFavorite) {
                        this.$eventBus.$emit('dialog-center', {
                            name: 'mvtConfirmSave',
                            trigger: this.$options.name,
                            opt: {},
                        });
                    }
                }
            });
        },
        initalDppNotification() {
            var that = this;
            var count = getStorage('dppCount');
            if (count >= 1) {
                count += 1;
            } else {
                count = 1;
            }
            if (!that.glb.user.id && !that.glb.isMobile && (count === 2 || count % 7 === 0)) {
                if (window.Notification && Notification.permission === 'granted') {
                    var searchUrl = '/' + this.dpp.geo.zipcodePath;
                    const propertyTypes = {
                        SINGLE_FAMILY_HOUSE: 'single-family',
                        CONDO: 'condos',
                        MULTI_FAMILY: 'multi-family',
                        LAND: 'land',
                        MOBILE: 'mobile',
                        OTHER: 'other',
                    };
                    searchUrl += `${propertyTypes[this.dpp.propertyType]}/`;

                    if (this.dpp.price) {
                        var maxPrice = (this.dpp.price * 1.2).toFixed(0);
                        searchUrl += `price-0-${maxPrice}/`;
                    }

                    if (this.dpp.bed) {
                        searchUrl += `bed-${this.dpp.bed}-0/`;
                    }
                    if (this.dpp.bath) {
                        searchUrl += `bath-${this.dpp.bath}-0/`;
                    }
                    var searchName = `${that.dpp.geo.state} ${that.dpp.geo.zipcode} ${that.dpp.propertyType}`;
                    this.createAccNoti = createNotification(
                        'Create Your Account',
                        'Get email updates on new and updated listings in ' + this.dpp.geo.zipcode + '.',
                        this.glb.cdnUrl + 'images/desktop/manifest/icon-192x192.png'
                    );
                    if (this.createAccNoti) {
                        this.createAccNoti.onclick = function () {
                            var data = {
                                userId: that.glb.user.id,
                                trigger: 'notification',
                                url: searchUrl,
                                searchName: searchName,
                                userType: that.glb.pageInfo.userType,
                                schoolRequest: this.getSchoolRequest,
                            };
                            that.addSaveSearch(data);
                            if (this.createAccNoti) {
                                this.createAccNoti.close();
                            }
                            that.$eventBus.$emit('ga', { en: 'click', ec: 'newaccount_notification' });
                        };
                    }
                }
            }
            updateSubscriptionByListing(this.dpp);
            setStorage('dppCount', count);
        },
        initalPageHistoryByStorage() {
            var prevPage = getStorage('currentPage');
            setStorage('prevPage', prevPage);
            setStorage('currentPage', 'vdpp');
            setStorage('lastViewedPropertyId', this.dpp.propertyId);
        },
        checkHashChange(event) {
            const ignoreHashChange = event && event.oldURL && event.oldURL.includes('#mvtDialog');
            const hash = location.hash;
            /* If coming from mvtDialog box, ignore scroll */
            if (hash && !ignoreHashChange) {
                scrollTo(hash.replace('#', ''));
            }
        },
        show3rdPartyForm({ id, url, isAppendUtmCta, cta, clickEvent }) {
            var pid;
            if (this.dpp.isActive && this.dpp.price <= 100000) {
                if (this.dpp.propertyType === 'MOBILE') {
                    pid = 'movoto_mobile';
                } else if (this.dpp.propertyType === 'LAND') {
                    pid = 'movoto_land';
                }
            }
            var payload = {
                pid: pid,
                id: id,
                url: url,
                cta: resolveClickCta(cta, clickEvent),
                zipcode: this.dpp.geo.zipcode,
                price: this.dpp.price,
                propertyType: this.dpp.propertyType,
                houseRealStatus: this.dpp.houseRealStatus,
                trigger: this.$options.name,
                imageUrl: this.dpp.tnImgPath,
                listingUrl: (this.dpp.path && `${this.glb.appUrl}${this.dpp.path}`) || '',
                isAppendUtmCta: isAppendUtmCta || false,
            };
            this.$eventBus.$emit('eventbus-pop-3rdparty', payload);
        },
        show3rdPartyForm1(id, url, cta = '', evt) {
            this.$eventBus.$emit('eventbus-pop-3rdparty', {
                id: id,
                url: url,
                cta: resolveClickCta(cta, evt, 'BTN'),
                zipcode: this.dpp.geo.zipcode,
                propertyType: this.dpp.propertyType,
                price: this.dpp.price,
                houseRealStatus: this.dpp.houseRealStatus,
                trigger: this.$options.name,
                listingUrl: (this.dpp.path && `${this.glb.appUrl}${this.dpp.path}`) || '',
            });
        },

        getBaseDataForSaveSearch() {
            const dict = {
                SINGLE_FAMILY_HOUSE: ['single-family', 'Single-Family'],
                MULTI_FAMILY: ['multi-family', 'Multi-family'],
                LAND: ['land', 'Lot/Land'],
                MOBILE: ['mobile', 'Mobile'],
                CONDO: ['condos', 'Condos'],
                OTHER: ['other', 'Other'],
            };
            let calculatedPrice = Math.round(this.dpp.price * 0.8);
            let url = `${this.dpp.geo.cityCode}-${this.dpp.geo.stateCode}/${dict[this.dpp.propertyType][0]}/price-${calculatedPrice}-0/`;
            if (this.dpp.bed) {
                if (this.dpp.bed <= 3) {
                    url += `bed-${this.dpp.bed}-0/`;
                } else if (this.dpp.bed > 3) {
                    url += `bed-${this.dpp.bed - 1}-0/`;
                }
            }
            const formatPrice = format.friendlyPrice;
            let searchName = `${this.dpp.geo.city} ${this.dpp.geo.state}, $${formatPrice(calculatedPrice)}+, ${dict[this.dpp.propertyType][1]}`;
            return {
                url,
                searchName,
            };
        },
        pageUnload() {
            if (this.createAccNoti) {
                this.createAccNoti.close();
            }
        },
        openDig(params) {
            var opt = params || {};
            var isPrOnly = this.dpp.isPrOnly && !this.dpp.realListingId;
            this.addClaimedHome({
                propertyId: this.dpp.propertyId,
                campaign: opt.campaign || 'claim-home',
                isPrOnly: isPrOnly,
                dppPath: this.dpp.path,
                authOptions: opt,
                propertyData: { tnImgPath: this.dpp.tnImgPath, mapUrls: this.dpp.mapUrls, photoCount: this.dpp.photoCount, geo: this.dpp.geo },
            }).then((result) => {
                if (!result) {
                    return;
                }
                window.location.assign(`${this.glb.appUrl}my-movoto/owned-homes/${this.dpp.propertyId}/`);
            });
        },
        openVeteransHotlead({ leadType }) {
            const deviceType = this.glb.isMobile ? '_Mobile' : '_Desktop';
            const hotleadType = leadType ? leadType + deviceType : 'veterandropdrawerlead';
            this.$eventBus.$emit('dialog-center', {
                name: 'mvtHotleadForm',
                trigger: this.$options.name,
                opt: {
                    hotleadTitle: 'Meet a Veterans Real Estate Agent',
                    hotleadType: hotleadType,
                    propertyId: this.dpp.propertyId,
                    propertyData: this.dpp,
                    showComment: false,
                    isVeteranDrawer: true,
                    ojoFormConfigs: {
                        showCtaLinks: true,
                        showOJOagentInfo: true,
                        showDisclaimer: false,
                        commnetLabel: 'Message',
                    },
                },
            });
        },
        toogleIACProvider() {
            this.openIACProvider = !this.openIACProvider;
            if (this.openIACProvider) {
                this.$eventBus.$emit('initialize-iacp-service');
            }
        },
        navigateToIACP() {
            this.$eventBus.$emit('ga', { ec: 'partner', en: 'telco_session', el: 'cordless', props: { link_name: 'save on internet and cable inactive' } });
            let that = this;
            setTimeout(function e() {
                that.$eventBus.$emit('expand-iacp');
            }, 10);
        },
        popContactUs() {
            this.$eventBus.$emit('dialog-open-contactus', { enquiryType: 5 });
        },
    },
    beforeMount() {
        // CW-6979 - event added to detect change in URL hash to expand the respective section
        window.addEventListener('hashchange', this.checkHashChange);
        window.addEventListener('beforeunload', this.pageUnload);

        if (!getCookie('mapsearchurl')) {
            // $('#body').removeClass('unscrolled');
            this.isNewUser = true;
        }
    },
    mounted() {
        var that = this;
        setTimeout(() => {
            this.split
                .fetchTreatments([PREAPPROVED_EXPERIMENT], {
                    attributes: {
                        movoto_geo_zipcode: this.dpp.geo.zipcode,
                        movoto_geo_state: this.dpp.geo.state,
                        movoto_listing_price: this.dpp.price,
                        movoto_listing_type: this.dpp.propertyType,
                        movoto_listing_status: this.dpp.houseRealStatus,
                    },
                })
                .then((treatment) => {
                    that.preapprovedTreatment = treatment;
                });
        }, 2000);
        if (this.getSplit('movoto-opendoor-panel-CW-4375')) {
            this.loadOpendoorAddressDetails();
        }
        this.addViewedPropertyId(that.dpp.propertyId);
        if (typeof window !== 'undefined' && window.location.hash) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    that.checkHashChange();
                });
            });
        }
        // Keep storage / lazyload / notifications / GA view_item / chat timers off the hydration stack (TBT).
        scheduleDeferredDppMountWork(() => {
            that._initalLazyload();
        });
        scheduleDeferredDppMountWork(() => {
            that.initalPageHistoryByStorage();
            that.setMediaPrice();
        });
        scheduleDeferredDppMountWork(() => {
            that.initalDppNotification();
        });
        scheduleDeferredDppMountWork(() => {
            that.viewItem();
            that.autoBottomChat();
        });
        // Defer agent fetch to idle (same pattern as `fetchAgents` in `search-list.js` mounted). Market chart data loads when `propertyMarket` / estimate chart hydrates.
        (typeof requestIdleCallback !== 'undefined' ? requestIdleCallback : (cb) => setTimeout(cb, 0))(() => {
            that.loadAssignedAgent();
        });
        this.componentMounted = true;
        $('.photoScroller').on('scroll', throttle(this.onLeftScroll, 200));
        $(document).on('dom.scroll.inistalHead', function () {
            if (!$.winStatus.isScrollDown && $.winStatus.scrollTop > 100) {
                $('#body').removeClass('unscrolled');
                $(document).off('dom.scroll.inistalHead');
            }
        });
        window.addEventListener(
            'scroll',
            throttle(() => {
                let body = document.getElementById('body');
                if (window.scrollY > 600 && !body.classList.contains('scrollDown')) {
                    body.classList.add('beforeScrollDown');
                    setTimeout(() => {
                        if (body.classList.contains('beforeScrollDown')) {
                            body.classList.add('scrollDown');
                        }
                    });
                } else if (window.scrollY < 600 && body.classList.contains('scrollDown')) {
                    body.classList.remove('scrollDown');
                    setTimeout(() => {
                        if (!body.classList.contains('scrollDown')) {
                            body.classList.remove('beforeScrollDown');
                        }
                    }, 100);
                }
            }),
            100
        );

        this.$eventBus.$on('property-pop-schedule', this.openSchedule);
        this.$eventBus.$on('property-pop-hot', this.openHotDialog);
        this.$eventBus.$on('property-pop-avm-disclaimer3', this.openAvmDisclaimer3);
        this.$eventBus.$on('property-pop-avm-disclaimer4', this.openAvmDisclaimer4);
        this.$eventBus.$on('property-pop-photo', this.openPopPhoto);
        this.$eventBus.$on('property-pop-photo-single', this.openPopPhotoSingle);
        this.$eventBus.$on('property-pop-3d', this.openPop3d);
        this.$eventBus.$on('property-pop-opendoor', this.openPopOpendoor);
        this.$eventBus.$on('property-pop-map', this.openPopMap);
        this.$eventBus.$on('property-save-favorite', this.saveFavorite);
        this.$eventBus.$emit('eventbus-pageLoaded');
        this.$eventBus.$on('eventbus-pop-dig', this.openDig);
        this.$eventBus.$on('eventbus-pop-trans', this.showTransDialog);
        this.$eventBus.$on('eventbus-pop-showmortage', this.showForm);
        this.$eventBus.$on('eventbus-pop-cpc3way', function (params) {
            that.show3rdPartyForm({ id: params.id, url: params.url, clickEvent: params.clickEvent });
        });
        this.$eventBus.$on('eventbus-primary-intent', function () {
            window.location.href = MORTGAGE_LINK_REDIRECT_URL;
        });
        $('#propertyInternetProvider .mvt-collapse-link').on('click', function () {
            that.openIACProvider = !that.openIACProvider;
            if (that.openIACProvider) {
                that.$eventBus.$emit('initialize-iacp-service');
            }
        });
        this.$eventBus.$on('eventbus-own-up', (cta) => {
            this.$refs.popOwnup.show(cta);
        });
        addDebugBearTag('tag4', this.dpp.houseRealStatus);
        setTimeout(() => {
            this.initalAutoPop();
        }, 1000);
    },
    beforeUnmount() {
        this.$eventBus.$off('property-pop-photo');
        this.$eventBus.$off('property-pop-3d');
        this.$eventBus.$off('property-pop-opendoor');
        this.$eventBus.$off('property-pop-map');
        this.$eventBus.$off('property-save-favorite');
        this.$eventBus.$off('eventbus-pop-dig', this.openDig);
        $(document).off('dom.scroll.vdpp');
        $(document).off('dom.scroll.' + this.vnumber);
        $(document).off('dom.scroll.inistalHead');
        window.removeEventListener('hashchange', this.checkHashChange);
        window.removeEventListener('beforeunload', this.pageUnload);
    },
};
